import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/authService';
import { toast } from 'sonner';
import {
  trackFeatureUsage,
  trackFormSubmission,
  trackEvent,
} from '@/analytics/AnalyticsTracker';

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
}

const ResetPasswordModal = ({
  isOpen,
  onClose,
  token,
}: ResetPasswordModalProps) => {
  const { t } = useTranslation('common');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  // Password validation
  const validatePassword = useCallback(
    (pwd: string) => {
      const errors: string[] = [];
      if (pwd.length < 6) errors.push(t('authModal.passwordRequirement.min'));
      if (!/[a-z]/.test(pwd))
        errors.push(t('authModal.passwordRequirement.lower'));
      if (!/[A-Z]/.test(pwd))
        errors.push(t('authModal.passwordRequirement.upper'));
      if (!/[0-9]/.test(pwd))
        errors.push(t('authModal.passwordRequirement.number'));
      if (!/[^a-zA-Z0-9]/.test(pwd))
        errors.push(t('authModal.passwordRequirement.special'));
      return errors;
    },
    [t],
  );

  // Validate password on change
  useEffect(() => {
    if (newPassword) {
      setPasswordErrors(validatePassword(newPassword));
    } else {
      setPasswordErrors([]);
    }
  }, [newPassword, validatePassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword.trim()) {
      toast.error(t('password.newPasswordRequired'));
      return;
    }

    if (passwordErrors.length > 0) {
      toast.error(t('password.fixPasswordRequirements'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('password.passwordsDoNotMatch'));
      return;
    }

    // Track form submission attempt
    trackFormSubmission('reset_password_form', 'reset_password_modal', {
      has_token: !!token,
      password_errors: passwordErrors.length,
      passwords_match: newPassword === confirmPassword,
    });

    setIsLoading(true);

    try {
      const response = await authService.resetPassword({
        token,
        newPassword: newPassword.trim(),
      });

      // Track successful reset
      trackEvent('reset_password_success', {
        token_present: !!token,
      });

      toast.success(response.message || t('password.resetSuccess'));

      // Reset form and close modal
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
      onClose();
    } catch (error) {
      console.error('Reset password failed:', error);

      // Track error
      trackEvent('reset_password_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
        token_present: !!token,
      });

      toast.error(
        error instanceof Error ? error.message : t('password.resetFailed'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPasswordToggle = () => {
    // Track password visibility toggle
    trackFeatureUsage('reset_password_modal', 'new_password_toggle', {
      action: showNewPassword ? 'hide' : 'show',
    });

    setShowNewPassword(!showNewPassword);
  };

  const handleConfirmPasswordToggle = () => {
    // Track password visibility toggle
    trackFeatureUsage('reset_password_modal', 'confirm_password_toggle', {
      action: showConfirmPassword ? 'hide' : 'show',
    });

    setShowConfirmPassword(!showConfirmPassword);
  };

  const isFormValid =
    newPassword &&
    confirmPassword &&
    passwordErrors.length === 0 &&
    newPassword === confirmPassword;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className='sm:max-w-md bg-gray-900 border-purple-500/20 [&>button]:w-8 [&>button]:h-8 [&>button]:text-gray-400 [&>button]:hover:text-red-400 [&>button]:hover:bg-red-500/10 [&>button]:rounded-md [&>button]:transition-colors [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:p-0'
        onPointerDownOutside={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className='text-center text-xl font-bold text-white pe-12'>
            {t('password.createNewPassword')}
          </DialogTitle>
          <DialogDescription className='text-center text-gray-400'>
            {t('password.enterNewPassword')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='new-password' className='text-gray-300'>
              {t('password.newPassword')}
            </Label>
            <div className='relative'>
              <Lock className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <Input
                id='new-password'
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className='ps-10 pe-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500'
                placeholder={t('password.enterNewPassword')}
                required
                autoComplete='new-password'
              />
              <button
                type='button'
                onClick={handleNewPasswordToggle}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300'
              >
                {showNewPassword ? (
                  <EyeOff className='w-4 h-4' />
                ) : (
                  <Eye className='w-4 h-4' />
                )}
              </button>
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='confirm-password' className='text-gray-300'>
              {t('password.confirmPassword')}
            </Label>
            <div className='relative'>
              <Lock className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <Input
                id='confirm-password'
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className='ps-10 pe-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500'
                placeholder={t('password.confirmNewPassword')}
                required
                autoComplete='new-password'
              />
              <button
                type='button'
                onClick={handleConfirmPasswordToggle}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300'
              >
                {showConfirmPassword ? (
                  <EyeOff className='w-4 h-4' />
                ) : (
                  <Eye className='w-4 h-4' />
                )}
              </button>
            </div>
          </div>

          {passwordErrors.length > 0 && (
            <div className='space-y-1'>
              <p className='text-sm text-red-400'>
                {t('password.passwordRequirements')}
              </p>
              <ul className='text-xs text-red-400 space-y-1'>
                {passwordErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {newPassword &&
            confirmPassword &&
            newPassword !== confirmPassword && (
              <p className='text-sm text-red-400'>
                {t('password.passwordsDoNotMatch')}
              </p>
            )}

          <div className='flex items-start space-x-2 rtl:space-x-reverse p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-md'>
            <AlertTriangle className='w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5' />
            <p className='text-xs text-yellow-400'>
              {t('password.passwordRequirementsDescription')}
            </p>
          </div>

          <Button
            type='submit'
            className='w-full'
            disabled={isLoading || !isFormValid}
          >
            {isLoading
              ? t('password.creating')
              : t('password.createNewPassword')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordModal;
